import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pickDefaults, pickStyle, previewStyle, setSelection, unpickStyle } from './configurator.api';
import { configuratorKeys } from './configurator.queryKeys';
import { useParams } from 'react-router';
import { ConfiguratorData, ConfiguratorOptionDto } from './configurator.types';

/**
 * Configurator state as-if the style were picked (backend in-memory simulation).
 * Keyed under the unit's configurator prefix, so any configurator mutation invalidates
 * it together with the current-state queries (the preview baseline is the current state).
 */
export function useStylePreviewQuery(residentialUnitId: number, styleId: number | undefined, roomId: number | null) {
    return useQuery<ConfiguratorData>({
        queryKey: configuratorKeys.stylePreview(residentialUnitId, styleId ?? 0, roomId),
        queryFn: residentialUnitId && styleId ? () => previewStyle(residentialUnitId, styleId, roomId) : skipToken,
        // Every local mutation invalidates the byUnit prefix, so a cached preview is only stale
        // after remote changes; don't refetch on every dialog re-open of an unchanged preview.
        staleTime: 5 * 60 * 1000,
    });
}

export function useConfiguratorMutations(residentialUnitId: number, selectedDeadlineId: number) {
    const queryClient = useQueryClient();
    const { roomId } = useParams<{ roomId: string }>();

    const { mutate: selectOption, isPending: isSelecting } = useMutation({
        mutationFn: async ({
            option,
            customFieldData,
        }: {
            option: ConfiguratorOptionDto;
            customFieldData: string | null;
        }) => {
            return await setSelection(residentialUnitId, selectedDeadlineId, {
                GroupId: option.optionGroupId,
                SelectedOptionId: option.id,
                // Room id is 0, when the "general" room is selected.
                RoomId: roomId === '0' ? null : Number(roomId),
                CustomFieldData: customFieldData ?? null,
                ManualQuantity: null,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: configuratorKeys.byUnit(residentialUnitId) });
        },
    });

    return { selectOption, isSelecting };
}

export function useSetPresetMutation() {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: (params: Parameters<typeof pickStyle>) => pickStyle(...params),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: configuratorKeys.byUnit(variables[0]) });
        },
    });
    return mutation;
}

export function useUnpickPresetMutation() {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: (params: Parameters<typeof unpickStyle>) => unpickStyle(...params),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: configuratorKeys.byUnit(variables[0]) });
        },
    });
    return mutation;
}

export function usePickDefaultsMutation(residentialUnitId: number, selectedDeadlineId: number) {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: () => pickDefaults(residentialUnitId, selectedDeadlineId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: configuratorKeys.byUnit(residentialUnitId) });
        },
    });
    return mutation;
}
